/**
 * services/migrations/migrateLocalSyncSettings.ts
 * One-time migration from client-side (IndexedDB) to server-side (Supabase)
 *
 * Migrates existing users' sync settings from SyncSettings (IndexedDB)
 * to user_sync_settings (Supabase).
 *
 * Safe to call multiple times - only migrates if server settings are pristine.
 * ChatGPT-approved: "Most configured wins" - prefer meaningful config over defaults.
 *
 * Architecture (Task 2.6 - Option A.1):
 * - Migrates universal preferences to Supabase (source of truth)
 * - Updates IndexedDB cache with lastServerSync timestamp (via write-through cache)
 * - Preserves directoryId in IndexedDB (device-specific, never synced to server)
 *
 * Task 2.6: Server-Side Sync Settings - Core Implementation
 */

import { SyncSettingsService } from '@/services/SyncSettingsService';
import { UserSyncSettingsService } from '@/services/UserSyncSettingsService';

/**
 * Migrate sync settings from client-side (IndexedDB) to server-side (Supabase)
 *
 * One-time migration for existing users who have settings in IndexedDB.
 * Safe to call multiple times - only migrates if server settings are pristine.
 *
 * Migration Logic (ChatGPT-approved):
 * 1. Check if server settings exist and have been modified (created_at !== updated_at)
 * 2. If modified, assume migration already done or user changed settings -> skip
 * 3. If pristine, check if local has meaningful configuration
 * 4. "Most configured wins" - only migrate if local has non-default values
 * 5. Keep IndexedDB data as fallback (don't delete)
 *
 * @param userId - User ID
 */
export async function migrateLocalSyncSettingsToServer(userId: string): Promise<void> {
  const localService = new SyncSettingsService();
  const serverService = new UserSyncSettingsService();

  try {
    // Get or create server settings
    const serverSettings = await serverService.getOrCreateSettings(userId);

    // Check if migration already done or user has modified settings
    // If server settings were updated, assume migration complete or user changed settings
    if (serverSettings.createdAt !== serverSettings.updatedAt) {
      console.log('⏭️  Server sync settings already migrated or modified by user');
      return;
    }

    // Get local settings from IndexedDB
    const localSettings = await localService.getAll();

    if (localSettings.length === 0) {
      console.log('⏭️  No local sync settings to migrate');
      return;
    }

    const local = localSettings[0];
    if (!local) {
      console.log('⏭️  Local settings are undefined');
      return;
    }

    // Check if local has meaningful configuration
    // "Most configured wins" - don't migrate if local is just defaults
    const hasMeaningfulConfig =
      local.activeSyncProvider !== null ||
      local.syncEnabled === true;

    if (!hasMeaningfulConfig) {
      console.log('⏭️  Local settings are defaults, skipping migration');
      return;
    }

    // Migrate to server (source of truth)
    // Note: updateSettings() automatically updates IndexedDB cache via write-through,
    // which sets lastServerSync timestamp and preserves directoryId
    await serverService.updateSettings(userId, {
      activeSyncProvider: local.activeSyncProvider,
      syncEnabled: local.syncEnabled,
      syncInterval: local.syncInterval,
      syncOnSessionClose: local.syncOnSessionClose,
      syncOnLogin: local.syncOnLogin,
      conflictResolution: local.conflictResolution
    });

    console.log('✅ Migrated local sync settings to server');
    console.log(`   Provider: ${local.activeSyncProvider || 'null'}`);
    console.log(`   Sync Enabled: ${local.syncEnabled}`);
    console.log(`   IndexedDB cache updated with lastServerSync timestamp`);

    // IndexedDB now serves as offline cache + device-specific directoryId storage
    // directoryId is preserved, universal prefs are cached for offline access
  } catch (error) {
    console.error('❌ Failed to migrate sync settings:', error);
    // Don't throw - allow app to continue with defaults
    // This ensures app doesn't break if migration fails
  }
}
